import { useState } from 'react';

import { Drawer } from '@/components/feedback/drawer';

import type { DeviceFilters, DeviceTypeFacet } from '../filtering';
import type { DeviceStatus, SubscriptionStatus } from '../types';

import styles from './device-filter-drawer.module.scss';

const TYPE_OPTIONS: Array<{ value: DeviceTypeFacet; label: string }> = [
  { value: 'Electric', label: 'Electric' },
  { value: 'Fuel', label: 'Fuel' },
  { value: 'Hawk', label: 'Hawk' },
  { value: 'Sensor', label: 'Sensor' },
];

const STATUS_OPTIONS: Array<{ value: DeviceStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'idle', label: 'Idle' },
  { value: 'disconnected', label: 'Disconnected' },
];

const SUB_OPTIONS: Array<{ value: SubscriptionStatus; label: string }> = [
  { value: 'active', label: 'Active' },
  { value: 'expiring_soon', label: 'Expiring Soon' },
  { value: 'none', label: 'No Active Subscription' },
];

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

type FacetGroupProps<T extends string> = {
  legend: string;
  options: Array<{ value: T; label: string }>;
  selected: T[];
  onToggle: (value: T) => void;
};

function FacetGroup<T extends string>({ legend, options, selected, onToggle }: FacetGroupProps<T>) {
  return (
    <fieldset className={styles.group}>
      <legend className={styles.legend}>{legend}</legend>
      <div className={styles.chips}>
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={active}
              className={[styles.chip, active ? styles.active : undefined]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onToggle(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export type DeviceFilterDrawerProps = {
  open: boolean;
  value: DeviceFilters;
  onClose: () => void;
  onApply: (filters: DeviceFilters) => void;
};

/** Right-side filter drawer (Figma 2249-713420). Edits a local draft; Apply commits,
 *  Cancel/close reverts. OR within a group, AND across groups (handled by the page). */
export function DeviceFilterDrawer({ open, value, onClose, onApply }: DeviceFilterDrawerProps) {
  const [draft, setDraft] = useState<DeviceFilters>(value);

  // Re-sync the draft to the applied value when the drawer transitions to open.
  // (Render-time adjustment via a transition tracker — React's recommended
  // alternative to a setState-in-effect.)
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setDraft(value);
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Filter"
      primaryText="Apply"
      primaryVariant="neutral"
      onPrimary={() => onApply(draft)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <div className={styles.body}>
        <FacetGroup
          legend="Device Type"
          options={TYPE_OPTIONS}
          selected={draft.types}
          onToggle={(v) => setDraft((d) => ({ ...d, types: toggle(d.types, v) }))}
        />
        <FacetGroup
          legend="Device Status"
          options={STATUS_OPTIONS}
          selected={draft.statuses}
          onToggle={(v) => setDraft((d) => ({ ...d, statuses: toggle(d.statuses, v) }))}
        />
        <FacetGroup
          legend="Subscription Status"
          options={SUB_OPTIONS}
          selected={draft.subscriptions}
          onToggle={(v) => setDraft((d) => ({ ...d, subscriptions: toggle(d.subscriptions, v) }))}
        />
      </div>
    </Drawer>
  );
}
